import Swal from 'sweetalert2';

// Centralized so every confirm/error dialog in the app looks the same and
// matches index.css's theme, instead of each caller re-specifying the same
// colors/classes. These are CSS custom properties, not literal hex -- since
// the popup is appended into the document, they resolve against whichever
// theme is current (including a toggle after the popup is already open),
// with no extra wiring needed here.
const themed = Swal.mixin({
  background: 'var(--color-surface)',
  color: 'var(--color-ink)',
  confirmButtonColor: 'var(--color-accent)',
  cancelButtonColor: 'transparent',
  customClass: {
    popup: 'rounded-2xl border border-border2',
    title: 'font-display',
    cancelButton: 'border! border-border2! text-muted!',
    input: 'bg-surface2! text-ink! border! border-border2! shadow-none!',
  },
  buttonsStyling: true,
});

export async function confirmDelete(name) {
  const { isConfirmed } = await themed.fire({
    title: `Delete "${name}"?`,
    text: 'This cannot be undone.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: 'var(--color-bad)', // destructive action, not the usual accent
    cancelButtonText: 'Cancel',
  });
  return isConfirmed;
}

export async function confirmStop(name) {
  const { isConfirmed } = await themed.fire({
    title: `Stop syncing ${name}?`,
    text: 'The migration in progress for this account will be interrupted.',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Stop sync',
    confirmButtonColor: 'var(--color-warn)', // disruptive but not destructive like delete
    cancelButtonText: 'Cancel',
  });
  return isConfirmed;
}

export async function confirmDeltaSync(name) {
  const { isConfirmed } = await themed.fire({
    title: `Run delta sync for ${name}?`,
    text: 'Re-runs imapsync once for every already-completed account to catch mail that arrived during the migration window. No password reset or restore-wait is repeated.',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Run delta sync',
    cancelButtonText: 'Cancel',
  });
  return isConfirmed;
}

export async function promptEditUsername(current) {
  const { value, isConfirmed } = await themed.fire({
    title: 'Edit username',
    input: 'text',
    inputValue: current,
    inputPlaceholder: 'jane.doe',
    showCancelButton: true,
    confirmButtonText: 'Save',
    cancelButtonText: 'Cancel',
    inputValidator: value => {
      if (!value || !value.trim()) return 'Username is required';
      if (!/^[a-zA-Z0-9._-]+$/.test(value.trim()))
        return 'Only letters, numbers, dots, dashes and underscores allowed';
    },
  });
  return isConfirmed ? value.trim() : null;
}

export async function promptAddAccounts() {
  const { value, isConfirmed } = await themed.fire({
    title: 'Add accounts',
    input: 'textarea',
    inputPlaceholder: 'jane.doe\njohn.smith\nsupport',
    showCancelButton: true,
    confirmButtonText: 'Add',
    cancelButtonText: 'Cancel',
    inputValidator: value => {
      const usernames = (value || '').split('\n').map(s => s.trim()).filter(Boolean);
      if (!usernames.length) return 'Enter at least one username';
      const bad = usernames.find(u => !/^[a-zA-Z0-9._-]+$/.test(u));
      if (bad) return `Invalid username: ${bad}`;
    },
  });
  if (!isConfirmed) return null;
  return value.split('\n').map(s => s.trim()).filter(Boolean);
}

export function showError(message) {
  return themed.fire({
    title: 'Something went wrong',
    text: message,
    icon: 'error',
    confirmButtonText: 'OK',
  });
}
